import Drawer from "@mui/material/Drawer";

import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Collapse from "@mui/material/Collapse";
import KeyIcon from "@mui/icons-material/Key";
import CurrencyYenIcon from "@mui/icons-material/CurrencyYen";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import EngineeringIcon from "@mui/icons-material/Engineering";

import Box from "@mui/material/Box";

import { useEffect, useState } from "react";
import { useMediaQuery } from "@mui/material";
import { useAccount } from "@gu-corp/gu-wallet-connector";
import { useRoles } from "../../providers/ContractProvider";
import theme from "../../theme";
import useAuthorized from "../../hooks/useAuthorized";
import {
  useMobileSidebarState,
  useMobileSidebarVisibleState,
} from "../../providers/mobile-sidebar/MobileSidebarProvider";
import Unauthenticated from "../Layout/UnAuthenticated";
import { Link, Outlet, useLocation } from "react-router-dom";
import Authenticating from "../Layout/Authenticating";

const DRAWER_WIDTH = 240;
const DRAWER_MEDIUM_WIDTH = 200;

const LINKS = [
  {
    text: "Managers",
    href: "/administrator/managers",
    icon: ManageAccountsIcon,
  },
  {
    text: "Operators",
    href: "/administrator/operators",
    icon: EngineeringIcon,
  },
  { text: "Key Keepers", href: "/administrator/key-keepers", icon: KeyIcon },
  {
    text: "Ownerships",
    href: "/administrator/ownerships",
    icon: FormatListBulletedIcon,
  },
  { text: "Balances", href: "/administrator/balances", icon: CurrencyYenIcon },
];

const PROPOSAL_LINKS = [
  {
    text: "Cancel Lockup",
    href: "/administrator/proposals/cancel-lockup",
  },
  {
    text: "Extend Lockup Period",
    href: "/administrator/proposals/extend-lockup-period",
  },
  {
    text: "Transfer Token Ownership",
    href: "/administrator/proposals/transfer-token-ownership",
  },
  {
    text: "Grant Key Keeper",
    href: "/administrator/proposals/grant-key-keeper",
  },
  {
    text: "Revoke Key Keeper",
    href: "/administrator/proposals/revoke-key-keeper",
  },
  {
    text: "Transfer Ownership",
    href: "/administrator/proposals/transfer-ownership",
  },
];

export default function AdministratorLayout() {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    setOpen(!open);
  };
  const { ADMINISTRATOR_ROLE } = useRoles();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  const { account } = useAccount();
  const { authorized, loading } = useAuthorized(ADMINISTRATOR_ROLE, account);

  const { setVisible } = useMobileSidebarVisibleState();
  useEffect(() => {
    if (authorized) {
      setVisible(true);
    }
  }, [authorized, setVisible]);

  const { open: mobileOpen } = useMobileSidebarState();

  if (loading) {
    return <Authenticating />;
  }

  if (!authorized) {
    return <Unauthenticated />;
  }

  return (
    <>
      <Drawer
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            top: ["48px", "56px", "64px"],
            height: "auto",
            bottom: 0,
          },
          display: { xs: "block", sm: "none" },
        }}
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <Divider />
        <List>
          {LINKS.map(({ text, href, icon: Icon }) => (
            <ListItem key={href} disablePadding>
              <ListItemButton
                component={Link}
                to={href}
                selected={href == location.pathname}
                key={href}
              >
                <ListItemIcon>
                  <Icon />
                </ListItemIcon>
                <ListItemText primary={text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider sx={{ mt: "auto" }} />
      </Drawer>
      <Drawer
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: isMediumScreen ? DRAWER_MEDIUM_WIDTH : DRAWER_WIDTH,
            boxSizing: "border-box",
            top: ["48px", "56px", "64px"],
            height: "auto",
            bottom: 0,
          },
          display: { xs: "none", sm: "block" },
        }}
        variant="permanent"
        anchor="left"
        open={mobileOpen}
      >
        <Divider />
        <List>
          {LINKS.map(({ text, href, icon: Icon }) => (
            <ListItem key={href} disablePadding>
              <ListItemButton
                component={Link}
                to={href}
                selected={href == location.pathname}
                key={href}
              >
                <ListItemIcon>
                  <Icon />
                </ListItemIcon>
                <ListItemText primary={text} />
              </ListItemButton>
            </ListItem>
          ))}
          <ListItemButton onClick={handleClick}>
            <ListItemIcon>
              <HowToVoteIcon />
            </ListItemIcon>
            <ListItemText primary="Proposals" />
            {open ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {PROPOSAL_LINKS.map((link) => (
                <ListItemButton
                  sx={{ pl: 4 }}
                  component={Link}
                  to={link.href}
                  selected={link.href == location.pathname}
                  key={link.href}
                >
                  <ListItemText primary={link.text} />
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        </List>
        <Divider sx={{ mt: "auto" }} />
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "background.default",
          ml: `${
            isSmallScreen
              ? 0
              : isMediumScreen
              ? DRAWER_MEDIUM_WIDTH
              : DRAWER_WIDTH
          }px`,
          p: [3],
        }}
      >
        <Outlet />
      </Box>
    </>
  );
}
